import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import { AuthContext } from "../context/AuthContext";
import toast, { Toaster } from "react-hot-toast";
import { FaEdit, FaEye, FaInfoCircle, FaTrash } from "react-icons/fa";
import { Plus, Trash2 } from "lucide-react";
import { getPackagingItems } from "../api/ingredients";
import {
  getCostingSettings,
  updateFxRates,
  updateRoyaltyRules,
  updateCategoryRules,
  updateGlobalAssumptions,
  getThroughputOverhead,
  updateThroughputOverhead,
  createPackagingVariant,
  getAllPackagingVariants,
  updatePackagingVariant,
  deletePackagingVariant,
} from "../api/costingSettingsApi";
import NewIngredient from '../components/NewIngredient'
import { downloadBomExcel } from "../api/bomApi";
import { downloadSuppliersExcel } from "../api/suppliers";
import { generateId } from "../utils/generateId";
import BomUploadModal from "../modals/BomUploadModal";
import SuppliersUploadModal from "../modals/SuppliersUploadModal";
import PackagingVariantModal from "../modals/PackagingVariantModal";
import FXRatesSection from "../costSections/FXRatesSection";
import RoyaltyRulesSection from "../costSections/RoyaltyRulesSection";
import CategoryRulesSection from "../costSections/CategoryRulesSection";
import ChannelRules from "../costSections/ChannelRules";
import SegmentCategoryMappingSection from "../costSections/SegmentCategoryMappingSection";
import {
  CATEGORY_RULE_DEFAULT_ROWS,
  DEFAULT_FX_RATES,
  DEFAULT_ROYALTY_RULES,
} from "../constants/CostingSettingsConstants";
import { ITEM_CATEGORIES } from "../constants/itemCategories";
import BulkRule from "../costSections/BulkRule";
import PrivateLabelMarginTiersSection from "../costSections/PrivateLabelMarginTiersSection";
import PrivateLabelCustomerMappingSection from "../costSections/PrivateLabelCustomerMappingSection";
import TrademarkPrefixManagementSection from "../costSections/TrademarkPrefixManagementSection";
import RM_DB from "./RM_DB";
import MasterDataManager from "./MasterDataManager";

export default function CostingSettings() {
  const { user, canRead, canWrite } = useContext(AuthContext);
  const canManageCostingSettings = canWrite("CostingSettingsPage");
  const canManageBomDb = canWrite("CostingBOMDB");
  const ensureSettingsWriteAccess = (actionLabel = "perform this action") => {
    if (canManageCostingSettings) return true;
    toast.error(`You do not have permission to ${actionLabel}.`);
    return false;
  };
  const ensureBomWriteAccess = (actionLabel = "perform this action") => {
    if (canManageBomDb) return true;
    toast.error(`You do not have permission to ${actionLabel}.`);
    return false;
  };
  const [loading, setLoading] = useState(true);
  const [isEditingFxRates, setIsEditingFxRates] = useState(false);
  const [originalFxRates, setOriginalFxRates] = useState(null);
  const [savingFxRates, setSavingFxRates] = useState(false);
  const [isEditingRoyaltyRules, setIsEditingRoyaltyRules] = useState(false);
  const [originalRoyaltyRules, setOriginalRoyaltyRules] = useState(null);
  const [savingRoyaltyRules, setSavingRoyaltyRules] = useState(false);
  const [isEditingCategoryRules, setIsEditingCategoryRules] = useState(false);
  const [originalCategoryRules, setOriginalCategoryRules] = useState(null);
  const [savingCategoryRules, setSavingCategoryRules] = useState(false);
  const [isEditingGlobalAssumptions, setIsEditingGlobalAssumptions] =
    useState(false);
  const [originalGlobalAssumptions, setOriginalGlobalAssumptions] =
    useState(null);
  const [savingGlobalAssumptions, setSavingGlobalAssumptions] = useState(false);
  const [isEditingThroughputOverhead, setIsEditingThroughputOverhead] =
    useState(false);
  const [originalThroughputOverhead, setOriginalThroughputOverhead] =
    useState([]);
  const [savingThroughputOverhead, setSavingThroughputOverhead] =
    useState(false);

  const [selectedBulkSize, setSelectedBulkSize] = useState("1kg");
  const [ingredientsList, setIngredientsList] = useState([]);
  const [activePackagingTab, setActivePackagingTab] = useState("Bulk");
  const [currentVariantName, setCurrentVariantName] = useState("");
  const [currentVariantIndex, setCurrentVariantIndex] = useState(0);
  const [savingPackaging, setSavingPackaging] = useState(false);
  const [allVariants, setAllVariants] = useState([]);
  const [loadingAllVariants, setLoadingAllVariants] = useState(false);
  const [currentVariantsPage, setCurrentVariantsPage] = useState(1);
  const [variantFormatFilter, setVariantFormatFilter] = useState("All");

  const [editingVariant, setEditingVariant] = useState(null);
  const [isEditingVariant, setIsEditingVariant] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [items, setItems] = useState([]);
  const [savingVariant, setSavingVariant] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [variantToDelete, setVariantToDelete] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewVariantData, setViewVariantData] = useState(null);
  const [showBomModal, setShowBomModal] = useState(false);
  const [showSuppliersModal, setShowSuppliersModal] = useState(false);
  const [downloadingBom, setDownloadingBom] = useState(false);
  const [downloadingSuppliers, setDownloadingSuppliers] = useState(false);
  const [loosePackAllowed, setLoosePackAllowed] = useState("no");
  const [defaultLooseUnits, setDefaultLooseUnits] = useState(0);
  const [defaultCasePack, setDefaultCasePack] = useState(1);
  const [defaultInnerMaster, setDefaultInnerMaster] = useState(0);
  const [isPackagingModalOpen, setIsPackagingModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [editingVariantForModal, setEditingVariantForModal] = useState(null);
  const [viewVariantModalOpen, setViewVariantModalOpen] = useState(false);
  const [viewingVariant, setViewingVariant] = useState(null);
  const packagingSetupRef = useRef(null);
  const [accordionOpen, setAccordionOpen] = useState({
    categoryRules: false,
    channelRules: false,
    bulkRules: false,
    privateLabelMarginTiers: false,
    privateLabelCustomerMapping: false,
    trademarksAndPrefixes: false,
    throughputOverhead: false,
    allPackagingVariants: false,
    segmentCategoryMappings: false,
  });

  const [fxRates, setFxRates] = useState([]);

  const [royaltyRules, setRoyaltyRules] = useState([]);
  const [categoryRules, setCategoryRules] = useState([]);

  const [globalAssumptions, setGlobalAssumptions] = useState({
    fxBufferPercent: 3,
    lossBufferPercent: 2,
    earlyPaymentDeductionPercent: 2,
    cogsIncreaseBufferPercent: 5,
    workingDaysPerWeek: 5,
    weeklyOverheadCostCad: 30200,
    weeklyLabourCostCad: 38000,
    confidentialCostInflationPercent: 0,
    hideDetailedCostsByDefault: false,
  });
  const [throughputOverhead, setThroughputOverhead] = useState([]);
  const [segmentCategoryMappings, setSegmentCategoryMappings] = useState({ bulk: [], retail: [], privatelabel: [] });

  const [packagingBOM, setPackagingBOM] = useState({
    Bulk: {
      "1kg": [],
      "2.5kg": [],
      "5kg": [],
      "10kg": [],
      "Master case": [],
    },
    Glass: [],
    Pouch: [],
  });

  const [dimensions, setDimensions] = useState({
    length: { mm: "", in: "" },
    width: { mm: "", in: "" },
    height: { mm: "", in: "" },
  });
  const [palletConfig, setPalletConfig] = useState({ ti: "", hi: "" });

  const MM_PER_INCH = 25.4;
  const packagingItemTypeOptions = [
    "PRIMARY PACKAGING SKU",
    "COVER SKU",
    "SAFETY SEAL SKU",
    "PRIMARY LABEL (F) SKU",
    "BACK LABEL (B) SKU",
    "TOP COVER LABEL (C) SKU",
    "BUNDLE PACK SKU",
    "SIFTER DISC SKU",
    "UNIT SHRINK WRAP SKU",
    "SHIPPING BOX SKU",
    "PARTITION SKU",
    "BOX LABEL SKU",
    "TRAY SKU",
    "MASTER SHRINK WRAP SKU",
    "BOX LINER SKU",
    "MASTER SHIPPING BOX SKU",
    "LOOSE PACKING SHIPPING BOX SKU",
  ].map((label) => ({ value: label, label }));

  const roundTo3Decimals = (num) => {
    if (typeof num !== "number" || isNaN(num)) return "";
    return Number(num.toFixed(3));
  };

  const categoryRuleOptions = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...(Array.isArray(ITEM_CATEGORIES) ? ITEM_CATEGORIES : [])
              .map((item) => String(item?.en || "").trim())
              .filter(Boolean),
            ...(Array.isArray(CATEGORY_RULE_DEFAULT_ROWS)
              ? CATEGORY_RULE_DEFAULT_ROWS
              : []
            )
              .map((row) => String(row?.category || "").trim())
              .filter(Boolean),
          ],
        ),
      ),
    []
  );

  const categoryRuleDefaultsByCategory = useMemo(
    () =>
      (Array.isArray(CATEGORY_RULE_DEFAULT_ROWS) ? CATEGORY_RULE_DEFAULT_ROWS : []).reduce(
        (acc, row) => {
          const category = String(row?.category || "").trim();
          if (!category) return acc;
          acc[category] = {
            signaturePremiumPerUnit: Math.max(
              0,
              Number(row.signaturePremiumPerUnit) || 0,
            ),
            allergenPremiumPerUnit: Math.max(
              0,
              Number(row.allergenPremiumPerUnit) || 0,
            ),
            minRetailSrpMargin: Math.max(0, Number(row.minRetailSrpMargin) || 0),
          };
          return acc;
        },
        {},
      ),
    [],
  );

 
  const normalizeFxRates = (rows = []) =>
    (Array.isArray(rows) ? rows : [])
      .map((row = {}) => ({
        currency: String(row.currency || "").trim().toUpperCase(),
        actualFxVsCad: Math.max(0, Number(row.actualFxVsCad) || 0),
        bufferedFxVsCad: Math.max(0, Number(row.bufferedFxVsCad) || 0),
      }))
      .filter((row) => row.currency);

  const normalizeRoyaltyRules = (rows = []) =>
    (Array.isArray(rows) ? rows : [])
      .map((row = {}) => ({
        royaltyRegion: String(row.royaltyRegion || "").trim(),
        royaltyRule: String(row.royaltyRule || "").trim(),
        royaltyPercent: Math.min(100, Math.max(0, Number(row.royaltyPercent) || 0)),
      }))
      .filter((row) => row.royaltyRegion && row.royaltyRule);

  const normalizeCategoryRules = (rows = []) =>
    (Array.isArray(rows) ? rows : [])
      .map((row = {}) => ({
        category: String(row.category || "").trim(),
        signaturePremiumPerUnit: Math.max(
          0,
          Number(row.signaturePremiumPerUnit) || 0,
        ),
        allergenPremiumPerUnit: Math.max(
          0,
          Number(row.allergenPremiumPerUnit) || 0,
        ),
        minRetailSrpMargin: Math.max(0, Number(row.minRetailSrpMargin) || 0),
      }))
      .filter((row) => row.category);



  const normalizeThroughputRow = (row = {}) => ({
    packagingType: String(row.packagingType || "").trim(),
    overheadAllocationPercent: Math.min(
      1,
      Math.max(0, Number(row.overheadAllocationPercent) || 0),
    ),
    labourAllocationPercent: Math.min(
      1,
      Math.max(0, Number(row.labourAllocationPercent) || 0),
    ),
    throughputUnitsPerDay: Math.max(0, Number(row.throughputUnitsPerDay) || 0),
  });

  const normalizeThroughputRows = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map(normalizeThroughputRow);

  const startEditingFxRates = () => {
    if (!ensureSettingsWriteAccess("edit FX rates")) return;
    setOriginalFxRates(fxRates.map((row) => ({ ...row })));
    setIsEditingFxRates(true);
  };

  const cancelEditingFxRates = () => {
    if (originalFxRates) {
      setFxRates(originalFxRates);
    }
    setIsEditingFxRates(false);
    setSavingFxRates(false);
  };

  const startEditingRoyaltyRules = () => {
    if (!ensureSettingsWriteAccess("edit royalty rules")) return;
    setOriginalRoyaltyRules(royaltyRules.map((row) => ({ ...row })));
    setIsEditingRoyaltyRules(true);
  };

  const cancelEditingRoyaltyRules = () => {
    if (originalRoyaltyRules) {
      setRoyaltyRules(originalRoyaltyRules);
    }
    setIsEditingRoyaltyRules(false);
    setSavingRoyaltyRules(false);
  };

  const startEditingCategoryRules = () => {
    if (!ensureSettingsWriteAccess("edit category rules")) return;
    setOriginalCategoryRules(categoryRules.map((row) => ({ ...row })));
    setIsEditingCategoryRules(true);
  };

  const cancelEditingCategoryRules = () => {
    if (originalCategoryRules) {
      setCategoryRules(originalCategoryRules);
    }
    setIsEditingCategoryRules(false);
    setSavingCategoryRules(false);
  };

  const startEditingGlobalAssumptions = () => {
    if (!ensureSettingsWriteAccess("edit global assumptions")) return;
    setOriginalGlobalAssumptions({ ...globalAssumptions });
    setIsEditingGlobalAssumptions(true);
  };

  const cancelEditingGlobalAssumptions = () => {
    if (originalGlobalAssumptions) {
      setGlobalAssumptions(originalGlobalAssumptions);
    }
    setIsEditingGlobalAssumptions(false);
    setSavingGlobalAssumptions(false);
  };

  const startEditingThroughputOverhead = () => {
    if (!ensureSettingsWriteAccess("edit throughput overhead settings")) return;
    setOriginalThroughputOverhead([...throughputOverhead]);
    setIsEditingThroughputOverhead(true);
  };

  const cancelEditingThroughputOverhead = () => {
    setThroughputOverhead([...originalThroughputOverhead]);
    setIsEditingThroughputOverhead(false);
    setSavingThroughputOverhead(false);
  };

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const result = await getCostingSettings();

        if (result.success && result.data) {
          const data = result.data;

          setFxRates(
            normalizeFxRates(data.fxRates || []).length > 0
              ? normalizeFxRates(data.fxRates || [])
              : DEFAULT_FX_RATES,
          );

          setRoyaltyRules(
            normalizeRoyaltyRules(data.royaltyRules || []).length > 0
              ? normalizeRoyaltyRules(data.royaltyRules || [])
              : DEFAULT_ROYALTY_RULES,
          );

          const normalizedCategoryRules = normalizeCategoryRules(
            data.categoryRules || [],
          );
          const existingByCategory = normalizedCategoryRules.reduce((acc, row) => {
            acc[row.category] = row;
            return acc;
          }, {});
          const optionRows = categoryRuleOptions.map((category) => ({
            category,
            signaturePremiumPerUnit:
              existingByCategory[category]?.signaturePremiumPerUnit ??
              categoryRuleDefaultsByCategory[category]?.signaturePremiumPerUnit ??
              0,
            allergenPremiumPerUnit:
              existingByCategory[category]?.allergenPremiumPerUnit ??
              categoryRuleDefaultsByCategory[category]?.allergenPremiumPerUnit ??
              0,
            minRetailSrpMargin:
              existingByCategory[category]?.minRetailSrpMargin ??
              categoryRuleDefaultsByCategory[category]?.minRetailSrpMargin ??
              0,
          }));
          const extraExistingRows = normalizedCategoryRules.filter(
            (row) => !categoryRuleOptions.includes(row.category),
          );
          setCategoryRules([...optionRows, ...extraExistingRows]);

          setGlobalAssumptions({
            fxBufferPercent: data.globalAssumptions?.fxBufferPercent ?? 3,
            lossBufferPercent: data.globalAssumptions?.lossBufferPercent ?? 2,
            earlyPaymentDeductionPercent:
              data.globalAssumptions?.earlyPaymentDeductionPercent ?? 2,
            cogsIncreaseBufferPercent:
              data.globalAssumptions?.cogsIncreaseBufferPercent ?? 5,
            workingDaysPerWeek: data.globalAssumptions?.workingDaysPerWeek ?? 5,
            weeklyOverheadCostCad:
              data.globalAssumptions?.weeklyOverheadCostCad ?? 30200,
            weeklyLabourCostCad:
              data.globalAssumptions?.weeklyLabourCostCad ?? 38000,
            confidentialCostInflationPercent:
              data.globalAssumptions?.confidentialCostInflationPercent ?? 0,
            hideDetailedCostsByDefault:
              Boolean(data.globalAssumptions?.hideDetailedCostsByDefault ?? false),
          });

          setThroughputOverhead(
            normalizeThroughputRows(data.throughputOverhead || []),
          );

          setPackagingBOM(
            data.packagingBOM || {
              Bulk: {
                "1kg": [
                  {
                    id: generateId(),
                    sku: "",
                    name: "",
                    qty: "",
                    unitCost: 0,
                  },
                ],
                "2.5kg": [
                  {
                    id: generateId(),
                    sku: "",
                    name: "",
                    qty: "",
                    unitCost: 0,
                  },
                ],
                "5kg": [
                  {
                    id: generateId(),
                    sku: "",
                    name: "",
                    qty: "",
                    unitCost: 0,
                  },
                ],
                "10kg": [
                  {
                    id: generateId(),
                    sku: "",
                    name: "",
                    qty: "",
                    unitCost: 0,
                  },
                ],
                "Master case": [
                  {
                    id: generateId(),
                    sku: "",
                    name: "",
                    qty: "",
                    unitCost: 0,
                  },
                ],
              },
              Glass: [
                {
                  id: generateId(),
                  sku: "",
                  name: "",
                  qty: "",
                  unitCost: 0,
                },
              ],
              Pouch: [
                {
                  id: generateId(),
                  sku: "",
                  name: "",
                  qty: "",
                  unitCost: 0,
                },
              ],
            },
          );

          setSegmentCategoryMappings(data.segmentCategoryMappings || { bulk: [], retail: [], privatelabel: [] });

          toast.success("Costing settings loaded successfully");
        } else {
          toast.error("Failed to load settings");
        }
      } catch (err) {
        toast.error("Error loading costing settings");
        console.error("Load settings error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    const loadThroughputOverheadSettings = async () => {
      try {
        const result = await getThroughputOverhead();
        if (result?.success) {
          setThroughputOverhead(
            normalizeThroughputRows(result.data?.throughputOverhead || []),
          );
        }
      } catch (err) {
        console.error("Failed to load throughput overhead settings:", err);
      }
    };
    loadThroughputOverheadSettings();
  }, []);

  useEffect(() => {
    const loadIngredients = async () => {
      try {
        const data = await getPackagingItems();
        setIngredientsList(data);
      } catch (err) {
        console.error("Failed to load packaging items");
      }
    };
    loadIngredients();
  }, []);

  useEffect(() => {
    const loadAllVariants = async () => {
      setLoadingAllVariants(true);
      try {
        const result = await getAllPackagingVariants();

        const variantsArray = result?.data || [];

        setAllVariants(variantsArray);
      } catch (err) {
        toast.error("Failed to load packaging variants");
        console.error("Load all variants error:", err);
        setAllVariants([]);
      } finally {
        setLoadingAllVariants(false);
      }
    };

    loadAllVariants();
  }, []);

  const filteredVariants = allVariants.filter((variant) =>
    variantFormatFilter === "All"
      ? true
      : variant.format === variantFormatFilter,
  );
  const sortedVariants = [...filteredVariants].sort((a, b) => {
  // First by category / format
  const formatCompare = String(a.format || "").localeCompare(
    String(b.format || "")
  );

  if (formatCompare !== 0) return formatCompare;

  // Then alphabetically by name
  return String(a.name || "").localeCompare(
    String(b.name || "")
  );
});
  const packagingVariantNameOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(allVariants) ? allVariants : [])
            .map((variant) => String(variant?.name || "").trim())
            .filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [allVariants],
  );

  useEffect(() => {
    const perPage = 15;
    const totalPages = Math.max(
      1,
      Math.ceil(filteredVariants.length / perPage),
    );
    setCurrentVariantsPage((prev) => Math.min(prev, totalPages));
  }, [filteredVariants.length]);

  const variantsPerPage = 15;
  const totalVariantPages = Math.max(
    1,
    Math.ceil(filteredVariants.length / variantsPerPage),
  );
  const visibleVariantStart = (currentVariantsPage - 1) * variantsPerPage;
  const paginatedVariants = filteredVariants.slice(
    visibleVariantStart,
    visibleVariantStart + variantsPerPage,
  );

  const handleFxRateChange = (index, field, value) => {
    setFxRates((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        if (field === "currency") {
          return { ...row, currency: String(value || "").trim().toUpperCase() };
        }
        return { ...row, [field]: Math.max(0, Number(value) || 0) };
      }),
    );
  };

  const handleAddFxRateRow = () => {
    setFxRates((prev) => [
      ...prev,
      { currency: "", actualFxVsCad: 0, bufferedFxVsCad: 0 },
    ]);
  };

  const handleRemoveFxRateRow = (index) => {
    setFxRates((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRoyaltyRuleChange = (index, field, value) => {
    setRoyaltyRules((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        if (field === "royaltyRegion" || field === "royaltyRule") {
          return { ...row, [field]: String(value || "") };
        }
        return {
          ...row,
          royaltyPercent: Math.min(100, Math.max(0, Number(value) || 0)),
        };
      }),
    );
  };

  const handleAddRoyaltyRuleRow = () => {
    setRoyaltyRules((prev) => [
      ...prev,
      { royaltyRegion: "", royaltyRule: "", royaltyPercent: 0 },
    ]);
  };

  const handleRemoveRoyaltyRuleRow = (index) => {
    setRoyaltyRules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCategoryRuleChange = (index, field, value) => {
    setCategoryRules((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        if (field === "category") {
          const category = String(value || "").trim();
          const defaults = categoryRuleDefaultsByCategory[category] || {};
          const currentSignature = Math.max(
            0,
            Number(row.signaturePremiumPerUnit) || 0,
          );
          const currentAllergen = Math.max(
            0,
            Number(row.allergenPremiumPerUnit) || 0,
          );
          const currentMinMargin = Math.max(0, Number(row.minRetailSrpMargin) || 0);
          return {
            ...row,
            category,
            signaturePremiumPerUnit:
              defaults.signaturePremiumPerUnit ?? currentSignature,
            allergenPremiumPerUnit:
              defaults.allergenPremiumPerUnit ?? currentAllergen,
            minRetailSrpMargin: defaults.minRetailSrpMargin ?? currentMinMargin,
          };
        }
        return { ...row, [field]: Math.max(0, Number(value) || 0) };
      }),
    );
  };

  const handleAddCategoryRuleRow = () => {
    setCategoryRules((prev) => [
      ...prev,
      {
        category: "",
        signaturePremiumPerUnit: 0,
        allergenPremiumPerUnit: 0,
        minRetailSrpMargin: 0,
      },
    ]);
  };

  const handleRemoveCategoryRuleRow = (index) => {
    setCategoryRules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGlobalAssumptionChange = (field, value, options = {}) => {
    if (typeof value === "boolean") {
      setGlobalAssumptions((prev) => ({
        ...prev,
        [field]: value,
      }));
      return;
    }
    if (value === "" || value === null || value === undefined) {
      setGlobalAssumptions((prev) => ({
        ...prev,
        [field]: "",
      }));
      return;
    }
    const { min = 0, max = Number.POSITIVE_INFINITY } = options;
    const parsed = Number(value);

    setGlobalAssumptions((prev) => ({
      ...prev,
      [field]: Number.isNaN(parsed) ? "" : Math.min(max, Math.max(min, parsed)),
    }));
  };

  const handleThroughputOverheadChange = (index, field, value) => {
    setThroughputOverhead((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        if (field === "packagingType") {
          return { ...row, packagingType: String(value || "") };
        }
        if (field === "throughputUnitsPerDay") {
          if (value === "" || value === null || value === undefined) {
            return { ...row, throughputUnitsPerDay: "" };
          }
          const parsed = Number(value);
          return {
            ...row,
            throughputUnitsPerDay: Number.isNaN(parsed) ? "" : Math.max(0, parsed),
          };
        }
        if (
          field === "overheadAllocationPercent" ||
          field === "labourAllocationPercent"
        ) {
          if (value === "" || value === null || value === undefined) {
            return { ...row, [field]: "" };
          }
          const asPercent = Number(value);
          return {
            ...row,
            [field]: Number.isNaN(asPercent)
              ? ""
              : Math.min(1, Math.max(0, asPercent / 100)),
          };
        }
        return row;
      }),
    );
  };

  const handleAddThroughputRow = () => {
    setThroughputOverhead((prev) => [
      ...prev,
      {
        packagingType: "",
        overheadAllocationPercent: 0,
        labourAllocationPercent: 0,
        throughputUnitsPerDay: 0,
      },
    ]);
  };

  const handleRemoveThroughputRow = (index) => {
    setThroughputOverhead((prev) => prev.filter((_, i) => i !== index));
  };

  const getThroughputRowDerivedValues = (row = {}) => {
    const workingDays = Math.max(1, Number(globalAssumptions.workingDaysPerWeek) || 1);
    const weeklyOverhead = Math.max(
      0,
      Number(globalAssumptions.weeklyOverheadCostCad) || 0,
    );
    const weeklyLabour = Math.max(
      0,
      Number(globalAssumptions.weeklyLabourCostCad) || 0,
    );
    const throughputUnits = Math.max(0, Number(row.throughputUnitsPerDay) || 0);
    const unitsPerWeek = throughputUnits * workingDays;

    const overheadCostPerUnit =
      unitsPerWeek > 0
        ? ((Number(row.overheadAllocationPercent) || 0) * weeklyOverhead) /
          unitsPerWeek
        : 0;
    const labourCostPerUnit =
      unitsPerWeek > 0
        ? ((Number(row.labourAllocationPercent) || 0) * weeklyLabour) /
          unitsPerWeek
        : 0;

    return {
      overheadCostPerUnit,
      labourCostPerUnit,
      totalCostPerUnit: overheadCostPerUnit + labourCostPerUnit,
    };
  };

  const toggleAccordion = (sectionKey) => {
    setAccordionOpen((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const startEditVariant = (variant) => {
    if (!ensureSettingsWriteAccess("edit packaging variants")) return;

    setEditingVariantForModal(variant);
    setModalMode("edit");
    setIsPackagingModalOpen(true);
  };

  const handleViewVariant = (variant) => {
    setViewVariantData(variant);
    setViewModalOpen(true);
  };

  const cancelEdit = () => {
    setIsCreating(false);
    setIsEditingVariant(false);
    setEditingVariant(null);
    setCurrentVariantName("");
    setItems([]);
    setDimensions({
      length: { mm: "", in: "" },
      width: { mm: "", in: "" },
      height: { mm: "", in: "" },
    });
    setPalletConfig({ ti: "", hi: "" });
    setLoosePackAllowed("no");
    setDefaultLooseUnits(0);
    setDefaultCasePack(0);
    setDefaultInnerMaster(0);
  };

  const handleDeleteVariant = (variant) => {
    if (!ensureSettingsWriteAccess("delete packaging variants")) return;
    setVariantToDelete(variant);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!ensureSettingsWriteAccess("delete packaging variants")) return;
    if (!variantToDelete) return;

    try {
      await deletePackagingVariant(variantToDelete._id);

      toast.success(`"${variantToDelete.name}" has been deleted`);

      // Refresh the list
      const result = await getAllPackagingVariants();
      setAllVariants(result?.data || result || []);

      // Close modal
      setDeleteModalOpen(false);
      setVariantToDelete(null);
    } catch (err) {
      toast.error("Failed to delete variant");
      console.error("Delete error:", err);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setVariantToDelete(null);
  };

  const handlePackagingPaste = (e, startIndex) => {
    e.preventDefault();
    const clipboardData = e.clipboardData.getData("text");
    const values = clipboardData
      .split(/[\n\r\t,]+/)
      .map((v) => v.trim())
      .filter(Boolean);

    if (values.length === 0) return;

    setItems((prev) => {
      const updated = [...prev];

      values.forEach((rawSku, i) => {
        const idx = startIndex + i;
        if (!updated[idx]) {
          updated.push({
            id: generateId(),
            itemType: "",
            sku: "",
            name: "",
            qty: "",
            unitCost: 0,
          });
        }

        const trimmedSku = rawSku.trim().toUpperCase();

        if (trimmedSku === "") {
          updated[idx].sku = "";
          updated[idx].name = "";
          updated[idx].unitCost = 0;
        } else {
          const found = ingredientsList.find(
            (ing) => ing.sku?.toUpperCase() === trimmedSku,
          );

          if (found) {
            updated[idx].sku = found.sku;
            updated[idx].name = found.name || found.customFields?.nameEn || "";
            updated[idx].unitCost = found.unitCost || 0;
          } else {
            // Invalid SKU -> store it but clear cost
            updated[idx].sku = rawSku;
            updated[idx].name = "";
            updated[idx].unitCost = 0;
          }
        }
      });

      return updated;
    });
  };

  const allergenSelectStyles = {
    option: (base, state) => ({
      ...base,
      backgroundColor:
        state.isFocused || state.isSelected ? "#f3f4f6" : "#ffffff",
      color: "#111827",
      ":active": {
        ...base[":active"],
        backgroundColor: "#e5e7eb",
      },
    }),
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadBomExcel = async () => {
    if (!ensureBomWriteAccess("download BOM data")) return;

    setDownloadingBom(true);
    try {
      const { blob, filename } = await downloadBomExcel();
      triggerBlobDownload(blob, filename || "bom-database.xlsx");
      toast.success("BOM Excel downloaded successfully");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "BOM download failed";
      toast.error(message);
    } finally {
      setDownloadingBom(false);
    }
  };

  const handleSaveFxRates = async () => {
    if (!ensureSettingsWriteAccess("save FX rates")) return;
    setSavingFxRates(true);
    try {
      const payload = normalizeFxRates(fxRates);
      const result = await updateFxRates(payload);
      if (result.success) {
        toast.success("FX rates saved successfully!");
        setIsEditingFxRates(false);
        if (result.data?.fxRates) {
          setFxRates(normalizeFxRates(result.data.fxRates));
        }
      } else {
        toast.error("Failed to save FX rates");
      }
    } catch (err) {
      toast.error("Error saving FX rates");
      console.error("Save FX rates error:", err);
    } finally {
      setSavingFxRates(false);
    }
  };

  const handleSaveRoyaltyRules = async () => {
    if (!ensureSettingsWriteAccess("save royalty rules")) return;
    setSavingRoyaltyRules(true);
    try {
      const payload = normalizeRoyaltyRules(royaltyRules);
      const result = await updateRoyaltyRules(payload);
      if (result.success) {
        toast.success("Royalty rules saved successfully!");
        setIsEditingRoyaltyRules(false);
        if (result.data?.royaltyRules) {
          setRoyaltyRules(normalizeRoyaltyRules(result.data.royaltyRules));
        }
      } else {
        toast.error("Failed to save royalty rules");
      }
    } catch (err) {
      toast.error("Error saving royalty rules");
      console.error("Save royalty rules error:", err);
    } finally {
      setSavingRoyaltyRules(false);
    }
  };

  const handleSaveCategoryRules = async () => {
    if (!ensureSettingsWriteAccess("save category rules")) return;
    setSavingCategoryRules(true);
    try {
      const payload = normalizeCategoryRules(categoryRules);
      const result = await updateCategoryRules(payload);
      if (result.success) {
        toast.success("Category rules saved successfully!");
        setIsEditingCategoryRules(false);
        if (result.data?.categoryRules) {
          setCategoryRules(normalizeCategoryRules(result.data.categoryRules));
        }
      } else {
        toast.error("Failed to save category rules");
      }
    } catch (err) {
      toast.error("Error saving category rules");
      console.error("Save category rules error:", err);
    } finally {
      setSavingCategoryRules(false);
    }
  };

  const handleSaveGlobalAssumptions = async () => {
    if (!ensureSettingsWriteAccess("save global assumptions")) return;
    setSavingGlobalAssumptions(true);
    try {
      const result = await updateGlobalAssumptions(globalAssumptions);
      if (result.success) {
        toast.success("Global assumptions saved successfully!");
        setIsEditingGlobalAssumptions(false);
        if (result.data?.globalAssumptions) {
          setGlobalAssumptions(result.data.globalAssumptions);
        }
      } else {
        toast.error("Failed to save global assumptions");
      }
    } catch (err) {
      toast.error("Error saving global assumptions");
      console.error("Save global assumptions error:", err);
    } finally {
      setSavingGlobalAssumptions(false);
    }
  };

  const handleSaveThroughputOverhead = async () => {
    if (!ensureSettingsWriteAccess("save throughput overhead settings")) return;
    setSavingThroughputOverhead(true);
    try {
      const payload = normalizeThroughputRows(throughputOverhead);
      const globalResult = await updateGlobalAssumptions({
        ...globalAssumptions,
        workingDaysPerWeek: Math.min(
          7,
          Math.max(1, Number(globalAssumptions.workingDaysPerWeek) || 1),
        ),
      });
      const result = await updateThroughputOverhead(payload);
      if (result.success && globalResult.success) {
        toast.success("Throughput overhead settings saved successfully!");
        setIsEditingThroughputOverhead(false);
        setThroughputOverhead(
          normalizeThroughputRows(result.data?.throughputOverhead || payload),
        );
        if (globalResult.data?.globalAssumptions) {
          setGlobalAssumptions(globalResult.data.globalAssumptions);
        }
      } else {
        toast.error("Failed to save throughput overhead settings");
      }
    } catch (err) {
      toast.error("Error saving throughput overhead settings");
      console.error("Save throughput overhead error:", err);
    } finally {
      setSavingThroughputOverhead(false);
    }
  };

  const handleDownloadSuppliersExcel = async () => {
    if (!ensureSettingsWriteAccess("download suppliers")) return;

    setDownloadingSuppliers(true);
    try {
      const { blob, filename } = await downloadSuppliersExcel();
      triggerBlobDownload(blob, filename || "suppliers.xlsx");
      toast.success("Suppliers Excel downloaded successfully");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Suppliers download failed";
      toast.error(message);
    } finally {
      setDownloadingSuppliers(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Toaster
        position="top-right"
        toastOptions={{ className: "font-medium" }}
      />

      <div className="mx-auto px-6 py-10">
        <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-10 mb-10">
          <h1 className="text-3xl font-bold text-gray-900">Costing Settings</h1>
          <p className="text-gray-600 mt-2 text-lg">
            Configure production costs and packaging materials. Save
            each section independently.
          </p>
        </div>
        {!canManageCostingSettings && (
          <div className="mb-8 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Read-only mode: view is enabled, but create/edit/delete and save
            actions are disabled.
          </div>
        )}



        <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-10 mb-10">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
                Suppliers Upload
              </h2>
              <p className="text-gray-600 mt-2">
                Upload the approved suppliers Excel file to update this system.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleDownloadSuppliersExcel}
                disabled={!canManageCostingSettings || downloadingSuppliers}
                className={`px-6 py-3 font-semibold rounded-xl transition shadow-md ${
                  canManageCostingSettings && !downloadingSuppliers
                    ? "bg-white text-gray-800 border border-gray-300 hover:bg-gray-50"
                    : "bg-gray-100 text-gray-500 border border-gray-200 cursor-not-allowed"
                }`}
              >
                {downloadingSuppliers
                  ? "Downloading..."
                  : "Download Current Suppliers"}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!ensureSettingsWriteAccess("upload suppliers")) return;
                  setShowSuppliersModal(true);
                }}
                disabled={!canManageCostingSettings}
                className={`px-6 py-3 font-semibold rounded-xl transition shadow-md flex items-center gap-2 ${
                  canManageCostingSettings
                    ? "bg-black text-white hover:bg-gray-900"
                    : "bg-gray-300 text-gray-600 cursor-not-allowed"
                }`}
              >
                Upload Suppliers Excel
              </button>
            </div>
          </div>
        </div>

        {showSuppliersModal && (
          <SuppliersUploadModal
            isOpen={showSuppliersModal}
            onClose={() => setShowSuppliersModal(false)}
            canUpload={canManageCostingSettings}
            onSuccess={() => {
              setShowSuppliersModal(false);
            }}
          />
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-10">
        <FXRatesSection
          fxRates={fxRates}
          isEditing={isEditingFxRates}
          saving={savingFxRates}
          canManage={canManageCostingSettings}
          onStartEdit={startEditingFxRates}
          onCancelEdit={cancelEditingFxRates}
          onSave={handleSaveFxRates}
          onChange={handleFxRateChange}
          onAddRow={handleAddFxRateRow}
          onRemoveRow={handleRemoveFxRateRow}
        />

        <RoyaltyRulesSection
          royaltyRules={royaltyRules}
          isEditing={isEditingRoyaltyRules}
          saving={savingRoyaltyRules}
          canManage={canManageCostingSettings}
          onStartEdit={startEditingRoyaltyRules}
          onCancelEdit={cancelEditingRoyaltyRules}
          onSave={handleSaveRoyaltyRules}
          onChange={handleRoyaltyRuleChange}
          onAddRow={handleAddRoyaltyRuleRow}
          onRemoveRow={handleRemoveRoyaltyRuleRow}
        />
        </div>

        {/* Global Assumptions */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-10 mb-10">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-10">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
                Global Assumptions
              </h2>
              <p className="text-gray-600 mt-2 text-lg">
                Admin-level buffers and weekly baseline costs that apply across
                all costing calculations.
              </p>
            </div>

            <div className="flex items-center gap-4">
              {isEditingGlobalAssumptions ? (
                <>
                  <button
                    onClick={cancelEditingGlobalAssumptions}
                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-2xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveGlobalAssumptions}
                    disabled={
                      savingGlobalAssumptions || !canManageCostingSettings
                    }
                    className={`px-6 py-3 font-semibold rounded-2xl flex items-center gap-3 transition-all shadow-lg active:scale-98 ${
                      savingGlobalAssumptions
                        ? "bg-gray-400 text-gray-100 cursor-not-allowed"
                        : "bg-black text-white hover:bg-gray-800"
                    }`}
                  >
                    {savingGlobalAssumptions ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={startEditingGlobalAssumptions}
                  disabled={!canManageCostingSettings}
                  className={`px-6 py-3 font-semibold rounded-2xl flex items-center gap-2 transition-all shadow-lg active:scale-98 ${
                    canManageCostingSettings
                      ? "bg-black text-white hover:bg-gray-800"
                      : "bg-gray-300 text-gray-600 cursor-not-allowed"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                  Edit
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Buffer Percentages */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                Risk & Buffer Settings
                <span className="text-amber-500 text-sm font-normal">
                  (Applied globally)
                </span>
              </h3>

              <div className="space-y-5">
                {[
                  {
                    label: "FX Buffer",
                    field: "fxBufferPercent",
                    suffix: "%",
                    tooltip: "Currency fluctuation buffer",
                  },
                  {
                    label: "Loss Buffer",
                    field: "lossBufferPercent",
                    suffix: "%",
                    tooltip: "Material loss during production",
                  },
                  {
                    label: "Early Payment Deduction",
                    field: "earlyPaymentDeductionPercent",
                    suffix: "%",
                  },
                  {
                    label: "COGS Increase Buffer",
                    field: "cogsIncreaseBufferPercent",
                    suffix: "%",
                    tooltip: "Expected cost increase over time",
                  },
                  {
                    label: "Confidential Price Factor",
                    field: "confidentialCostInflationPercent",
                    suffix: "%",
                    tooltip: "Applied as a percentage of the lowest calculated price level for confidential/restricted users (e.g. 90% of lowest price)",
                  },
                ].map((item) => (
                  <div
                    key={item.field}
                    className="bg-gray-50 border border-gray-100 rounded-2xl p-6"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-medium text-gray-900">
                          {item.label}
                        </p>
                        {item.tooltip && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {item.tooltip}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={globalAssumptions[item.field]}
                          onChange={(e) =>
                            handleGlobalAssumptionChange(
                              item.field,
                              e.target.value,
                            )
                          }
                          disabled={!isEditingGlobalAssumptions}
                          className={`w-28 text-right text-2xl font-semibold bg-transparent focus:bg-white rounded-xl px-4 py-2 border-2 transition-all ${
                            !isEditingGlobalAssumptions
                              ? "border-transparent text-gray-400"
                              : "border-gray-300 focus:border-black focus:ring-1 focus:ring-black"
                          }`}
                        />
                        <span className="text-gray-500 font-medium ml-1">
                          {item.suffix}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-900">
                        Hide Detailed Costs by Default
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        When enabled, non-admin users without explicit Detailed Costs permission cannot see ingredient unit costs or packaging item costs
                      </p>
                    </div>
                    <div>
                      <input
                        type="checkbox"
                        checked={Boolean(globalAssumptions.hideDetailedCostsByDefault)}
                        onChange={(e) =>
                          handleGlobalAssumptionChange(
                            "hideDetailedCostsByDefault",
                            e.target.checked
                          )
                        }
                        disabled={!isEditingGlobalAssumptions}
                        className="w-5 h-5 accent-black rounded cursor-pointer disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Operational Settings */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Operational Baseline
              </h3>

              <div className="space-y-5">
                {/* Working Days */}
                <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-900">
                        Working Days per Week
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Used for labour & overhead calculations
                      </p>
                    </div>
                    <input
                      type="number"
                      min="1"
                      max="7"
                      value={globalAssumptions.workingDaysPerWeek}
                      onChange={(e) =>
                        handleGlobalAssumptionChange(
                          "workingDaysPerWeek",
                          e.target.value,
                          { min: 1, max: 7 },
                        )
                      }
                      disabled={!isEditingGlobalAssumptions}
                      className={`w-24 text-center text-2xl font-bold bg-transparent rounded-2xl px-5 py-3 border-2 transition-all ${
                        !isEditingGlobalAssumptions
                          ? "border-transparent text-gray-400"
                          : "border-gray-300 focus:border-black"
                      }`}
                    />
                  </div>
                </div>

                {/* Weekly Costs */}
                {[
                  {
                    label: "Weekly Overhead Cost",
                    field: "weeklyOverheadCostCad",
                    tooltip:
                      "Fixed overhead expenses per week (rent, utilities, etc.)",
                  },
                  {
                    label: "Weekly Labour Cost",
                    field: "weeklyLabourCostCad",
                    tooltip: "Total weekly labour cost across all shifts",
                  },
                ].map((item) => (
                  <div
                    key={item.field}
                    className="bg-gray-50 border border-gray-100 rounded-2xl p-6"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">
                          {item.label} (CAD)
                        </p>
                        {item.tooltip && (
                          <p className="text-xs text-gray-500 mt-1">
                            {item.tooltip}
                          </p>
                        )}
                      </div>
                      <div className="relative">
                        <span className="absolute left-5 top-1/2 -translate-y-1/2 text-2xl text-gray-400 font-light">
                          $
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="100"
                          value={globalAssumptions[item.field]}
                          onChange={(e) =>
                            handleGlobalAssumptionChange(
                              item.field,
                              e.target.value,
                            )
                          }
                          disabled={!isEditingGlobalAssumptions}
                          className={`pl-10 w-52 text-3xl font-semibold bg-transparent rounded-2xl px-4 py-3 border-2 transition-all text-right ${
                            !isEditingGlobalAssumptions
                              ? "border-transparent text-gray-400"
                              : "border-gray-300 focus:border-black focus:ring-1 focus:ring-black"
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>


           

          {/* Optional: Help note when not editing */}
          {!isEditingGlobalAssumptions && (
            <div className="mt-8 text-center text-sm text-gray-500 bg-amber-50 border border-amber-100 rounded-2xl p-4">
              Click <span className="font-medium text-black">"Edit"</span> to
              modify these global values.
            </div>
          )}
        </div>

{/* <NewIngredient />
<RM_DB/> */}
<MasterDataManager/>

        <div className="bg-white rounded-2xl border border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => toggleAccordion("categoryRules")}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-2xl font-bold text-gray-900">Category Rules</h2>
            <span className="text-2xl text-gray-500 leading-none">
              {accordionOpen.categoryRules ? "-" : "+"}
            </span>
          </button>
          {accordionOpen.categoryRules && (
            <div className="px-4 pb-4">
              <CategoryRulesSection
                categoryRules={categoryRules}
                categoryOptions={categoryRuleOptions}
                isEditing={isEditingCategoryRules}
                saving={savingCategoryRules}
                canManage={canManageCostingSettings}
                onStartEdit={startEditingCategoryRules}
                onCancelEdit={cancelEditingCategoryRules}
                onSave={handleSaveCategoryRules}
                onChange={handleCategoryRuleChange}
                onAddRow={handleAddCategoryRuleRow}
                onRemoveRow={handleRemoveCategoryRuleRow}
                ensureSettingsWriteAccess={ensureSettingsWriteAccess}
              />
            </div>
          )}
        </div>

        {/* Segment Category Mapping */}
        <div className="bg-white rounded-2xl border border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => toggleAccordion("segmentCategoryMappings")}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-2xl font-bold text-gray-900">Segment Category Mapping</h2>
            <span className="text-2xl text-gray-500 leading-none">
              {accordionOpen.segmentCategoryMappings ? "-" : "+"}
            </span>
          </button>
          
          {accordionOpen.segmentCategoryMappings && (
            <div className="px-4 pb-4 border-t border-gray-100">
              <SegmentCategoryMappingSection
                segmentCategoryMappings={segmentCategoryMappings}
                availableCategories={categoryRuleOptions}
                ensureSettingsWriteAccess={ensureSettingsWriteAccess}
                onSettingsUpdated={() => {
                  getCostingSettings().then((res) => {
                    if (res.success) {
                      setSegmentCategoryMappings(res.data.segmentCategoryMappings || { bulk: [], retail: [], privatelabel: [] });
                    }
                  });
                }}
              />
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => toggleAccordion("channelRules")}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-2xl font-bold text-gray-900">Channel Rules</h2>
            <span className="text-2xl text-gray-500 leading-none">
              {accordionOpen.channelRules ? "-" : "+"}
            </span>
          </button>
          {accordionOpen.channelRules && (
            <div className="px-4 pb-4">
              <ChannelRules />
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => toggleAccordion("bulkRules")}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-2xl font-bold text-gray-900">Bulk Rules</h2>
            <span className="text-2xl text-gray-500 leading-none">
              {accordionOpen.bulkRules ? "-" : "+"}
            </span>
          </button>
          {accordionOpen.bulkRules && (
            <div className="px-4 pb-4">
              <BulkRule />
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => toggleAccordion("privateLabelMarginTiers")}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-2xl font-bold text-gray-900">Private Label Margin Tiers</h2>
            <span className="text-2xl text-gray-500 leading-none">
              {accordionOpen.privateLabelMarginTiers ? "-" : "+"}
            </span>
          </button>
          {accordionOpen.privateLabelMarginTiers && (
            <div className="px-4 pb-4">
              <PrivateLabelMarginTiersSection />
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => toggleAccordion("trademarksAndPrefixes")}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-2xl font-bold text-gray-900">Private Label Trademarks & Prefixes</h2>
            <span className="text-2xl text-gray-500 leading-none">
              {accordionOpen.trademarksAndPrefixes ? "-" : "+"}
            </span>
          </button>
          {accordionOpen.trademarksAndPrefixes && (
            <div className="px-4 pb-4">
              <TrademarkPrefixManagementSection />
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => toggleAccordion("privateLabelCustomerMapping")}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-2xl font-bold text-gray-900">Private Label Customer Mapping</h2>
            <span className="text-2xl text-gray-500 leading-none">
              {accordionOpen.privateLabelCustomerMapping ? "-" : "+"}
            </span>
          </button>
          {accordionOpen.privateLabelCustomerMapping && (
            <div className="px-4 pb-4">
              <PrivateLabelCustomerMappingSection />
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => toggleAccordion("throughputOverhead")}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-2xl font-bold text-gray-900">
              Throughput Overhead
            </h2>
            <span className="text-2xl text-gray-500 leading-none">
              {accordionOpen.throughputOverhead ? "-" : "+"}
            </span>
          </button>
          {accordionOpen.throughputOverhead && (
            <div className="px-4 pb-4">
              {/* Throughput Overhead */}
              <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-10 mb-10">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
               Production Costs
              </h2>
              <p className="text-gray-600 mt-2 text-sm">
                Define packaging throughput drivers. Unit overhead and labour are
                calculated from Global Assumptions.
              </p>
            </div>

            <div className="flex items-center gap-3">
              {isEditingThroughputOverhead ? (
                <>
                  <button
                    onClick={cancelEditingThroughputOverhead}
                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-2xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveThroughputOverhead}
                    disabled={
                      savingThroughputOverhead || !canManageCostingSettings
                    }
                    className={`px-6 py-3 font-semibold rounded-2xl flex items-center gap-3 transition-all shadow-lg active:scale-98 ${
                      savingThroughputOverhead
                        ? "bg-gray-400 text-gray-100 cursor-not-allowed"
                        : "bg-black text-white hover:bg-gray-800"
                    }`}
                  >
                    {savingThroughputOverhead ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={startEditingThroughputOverhead}
                  disabled={!canManageCostingSettings}
                  className={`px-6 py-3 font-semibold rounded-2xl flex items-center gap-2 transition-all shadow-lg active:scale-98 ${
                    canManageCostingSettings
                      ? "bg-black text-white hover:bg-gray-800"
                      : "bg-gray-300 text-gray-600 cursor-not-allowed"
                  }`}
                >
                  Edit
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto border border-gray-200 rounded-2xl">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">
                    Packaging Type
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Overhead Allocation %
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Labour Allocation %
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Throughput Units / Day
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Working Days / Week
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Overhead Cost / Unit (CAD)
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Labour Cost / Unit (CAD)
                  </th>
                  <th className="px-4 py-3 text-right font-semibold">
                    Total OH+Labour / Unit (CAD)
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {throughputOverhead.map((row, index) => {
                  const derived = getThroughputRowDerivedValues(row);
                  return (
                    <tr key={`throughput-row-${index}`} className="border-t">
                      <td className="px-4 py-3">
                        <select
                          value={row.packagingType}
                          onChange={(e) =>
                            handleThroughputOverheadChange(
                              index,
                              "packagingType",
                              e.target.value,
                            )
                          }
                          disabled={!isEditingThroughputOverhead}
                          className={`w-72 max-w-full px-3 py-2 rounded-lg border ${
                            isEditingThroughputOverhead
                              ? "border-gray-300 bg-white"
                              : "border-transparent bg-transparent text-gray-700 appearance-none"
                          }`}
                        >
                          <option value="">Select packaging variant</option>
                          {packagingVariantNameOptions.map((name) => {
                            const isSelected = throughputOverhead.some(
                              (r, i) => i !== index && r.packagingType === name
                            );
                            return (
                              <option key={name} value={name} disabled={isSelected}>
                                {name}
                              </option>
                            );
                          })}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={
                            row.overheadAllocationPercent === "" ||
                            row.overheadAllocationPercent === undefined ||
                            row.overheadAllocationPercent === null
                              ? ""
                              : Number(
                                  (
                                    Number(row.overheadAllocationPercent) * 100
                                  ).toFixed(4),
                                )
                          }
                          onChange={(e) =>
                            handleThroughputOverheadChange(
                              index,
                              "overheadAllocationPercent",
                              e.target.value,
                            )
                          }
                          disabled={!isEditingThroughputOverhead}
                          className={`w-28 px-3 py-2 rounded-lg border text-right ${
                            isEditingThroughputOverhead
                              ? "border-gray-300 bg-white"
                              : "border-transparent bg-transparent text-gray-700"
                          }`}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={
                            row.labourAllocationPercent === "" ||
                            row.labourAllocationPercent === undefined ||
                            row.labourAllocationPercent === null
                              ? ""
                              : Number(
                                  (
                                    Number(row.labourAllocationPercent) * 100
                                  ).toFixed(4),
                                )
                          }
                          onChange={(e) =>
                            handleThroughputOverheadChange(
                              index,
                              "labourAllocationPercent",
                              e.target.value,
                            )
                          }
                          disabled={!isEditingThroughputOverhead}
                          className={`w-28 px-3 py-2 rounded-lg border text-right ${
                            isEditingThroughputOverhead
                              ? "border-gray-300 bg-white"
                              : "border-transparent bg-transparent text-gray-700"
                          }`}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          min="0"
                          step="100"
                          value={
                            row.throughputUnitsPerDay === "" ||
                            row.throughputUnitsPerDay === undefined ||
                            row.throughputUnitsPerDay === null
                              ? ""
                              : row.throughputUnitsPerDay
                          }
                          onChange={(e) =>
                            handleThroughputOverheadChange(
                              index,
                              "throughputUnitsPerDay",
                              e.target.value,
                            )
                          }
                          disabled={!isEditingThroughputOverhead}
                          className={`w-36 px-3 py-2 rounded-lg border text-right ${
                            isEditingThroughputOverhead
                              ? "border-gray-300 bg-white"
                              : "border-transparent bg-transparent text-gray-700"
                          }`}
                        />
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 font-medium">
                        <input
                          type="number"
                          min="1"
                          max="7"
                          step="1"
                          value={
                            globalAssumptions.workingDaysPerWeek === "" ||
                            globalAssumptions.workingDaysPerWeek === undefined ||
                            globalAssumptions.workingDaysPerWeek === null
                              ? ""
                              : globalAssumptions.workingDaysPerWeek
                          }
                          onChange={(e) =>
                            handleGlobalAssumptionChange(
                              "workingDaysPerWeek",
                              e.target.value,
                              { min: 1, max: 7 },
                            )
                          }
                          disabled={!isEditingThroughputOverhead}
                          className={`w-20 px-3 py-2 rounded-lg border text-right ${
                            isEditingThroughputOverhead
                              ? "border-gray-300 bg-white"
                              : "border-transparent bg-transparent text-gray-700"
                          }`}
                        />
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        {derived.overheadCostPerUnit.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        {derived.labourCostPerUnit.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">
                        {derived.totalCostPerUnit.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveThroughputRow(index)}
                          disabled={!isEditingThroughputOverhead}
                          className={`inline-flex items-center justify-center p-2 rounded-lg ${
                            isEditingThroughputOverhead
                              ? "text-red-600 hover:bg-red-50"
                              : "text-gray-300 cursor-not-allowed"
                          }`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}


                 

                {throughputOverhead.length === 0 && (
                  <tr>
                    <td
                      className="px-4 py-8 text-center text-gray-500"
                      colSpan={9}
                    >
                      No throughput rows yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
              <div className="mt-6 flex justify-between items-center gap-3">
            <p className="text-sm text-gray-500">
              Costs per unit are computed from allocation %, throughput, and
              weekly global assumptions.
            </p>
            <button
              type="button"
              onClick={handleAddThroughputRow}
              disabled={!isEditingThroughputOverhead}
              className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 ${
                isEditingThroughputOverhead
                  ? "bg-black text-white hover:bg-gray-800"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}
            >
              <Plus className="w-4 h-4" />
              Add Row
            </button>
              </div>
            </div>
            </div>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => toggleAccordion("allPackagingVariants")}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h2 className="text-2xl font-bold text-gray-900">
              Packaging Setup
            </h2>
            <span className="text-2xl text-gray-500 leading-none">
              {accordionOpen.allPackagingVariants ? "-" : "+"}
            </span>
          </button>
          {accordionOpen.allPackagingVariants && (
            <div className="min-w-0 px-4 pb-4">
              <div className="mt-8 min-w-0 bg-white rounded-3xl shadow-lg border border-gray-200 p-4 sm:p-6 lg:p-8">
          <div className="flex min-w-0 flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
                All Packaging Variants
              </h2>
              <p className="text-gray-600 mt-2">
                Define all packaging formats and components used by the
                business.
              </p>
            </div>

            <div className="flex min-w-0 flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
              {/* Filter + Showing Info */}
              <div className="flex items-center gap-4 flex-1 lg:flex-none">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 font-medium whitespace-nowrap">
                    Filter
                  </label>
                  <select
                    value={variantFormatFilter}
                    onChange={(e) => {
                      setVariantFormatFilter(e.target.value);
                      setCurrentVariantsPage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                  >
                    <option value="All">All</option>
                    <option value="Bulk">Bulk</option>
                    <option value="Glass">Glass</option>
                    <option value="Pouch">Pouch</option>
                    <option value="Plastic">Plastic</option>
                  </select>
                </div>
              </div>
              {/* Create New Variant Button */}
              <button
                onClick={() => {
                  if (!ensureSettingsWriteAccess("create packaging variants"))
                    return;
                  setModalMode("create");
                  setEditingVariantForModal(null);
                  setIsPackagingModalOpen(true);
                }}
                disabled={!canManageCostingSettings}
                className={`px-6 py-3 font-semibold rounded-xl flex items-center gap-2 transition shadow-lg whitespace-nowrap ${
                  canManageCostingSettings
                    ? "bg-black text-white hover:bg-gray-800"
                    : "bg-gray-300 text-gray-600 cursor-not-allowed"
                }`}
              >
                <Plus className="w-5 h-5" />
                Create New Variant
              </button>
            </div>
          </div>

          {/* Packaging Variant Modal */}
          {/* <PackagingVariantModal
            isOpen={isPackagingModalOpen}
            onClose={() => {
              setIsPackagingModalOpen(false);
              setEditingVariantForModal(null);
              setModalMode("create");
            }}
            mode={modalMode}
            initialVariant={editingVariantForModal}
            onSuccess={() => {
              const loadAllVariants = async () => {
                try {
                  const result = await getAllPackagingVariants();
                  setAllVariants(result?.data || result || []);
                } catch (err) {
                  console.error("Failed to refresh variants");
                }
              };
              loadAllVariants();
            }}
            ingredientsList={ingredientsList}
            packagingItemTypeOptions={packagingItemTypeOptions}
            canManageCostingSettings={canManageCostingSettings}
            ensureSettingsWriteAccess={ensureSettingsWriteAccess}
          /> */}
          <PackagingVariantModal
  isOpen={isPackagingModalOpen}
  onClose={() => {
    setIsPackagingModalOpen(false);
    setEditingVariantForModal(null);
    setModalMode("create");
  }}
  mode={modalMode}
  initialVariant={editingVariantForModal}
  onSuccess={async () => {
    try {
      const result = await getAllPackagingVariants();
      const updatedList = result?.data || result || [];

      console.log("UPDATED LIST AFTER SAVE =>", updatedList);

      setAllVariants(updatedList);
      setEditingVariantForModal(null);
      setModalMode("create");
    } catch (err) {
      console.error("Failed to refresh variants", err);
    }
  }}
  ingredientsList={ingredientsList}
  packagingItemTypeOptions={packagingItemTypeOptions}
  canManageCostingSettings={canManageCostingSettings}
  ensureSettingsWriteAccess={ensureSettingsWriteAccess}
/>

          {loadingAllVariants ? (
  <div className="text-center py-16">
    <div className="inline-block w-10 h-10 border-4 border-gray-300 border-t-black rounded-full animate-spin"></div>
    <p className="mt-4 text-gray-600">Loading variants...</p>
  </div>
) : filteredVariants.length === 0 ? (
  <div className="text-center py-16 bg-gray-50 rounded-2xl">
    <p className="text-xl text-gray-600 mb-4">
      No packaging variants found for this filter.
    </p>
    <p className="text-gray-500">
      Try another format filter or create a new variant above.
    </p>
  </div>
) : (
  <>
    <div className="min-w-0 border border-gray-300 rounded-xl overflow-hidden bg-white">
      <div className="flex flex-col gap-2 px-5 py-4 border-b border-gray-300 bg-white sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600">
          Showing{" "}
          <span className="font-semibold text-gray-900">
            {visibleVariantStart + 1}
          </span>{" "}
          to{" "}
          <span className="font-semibold text-gray-900">
            {Math.min(
              visibleVariantStart + variantsPerPage,
              filteredVariants.length
            )}
          </span>{" "}
          of{" "}
          <span className="font-semibold text-gray-900">
            {filteredVariants.length}
          </span>{" "}
          variants
        </p>

        <p className="text-sm font-semibold text-gray-900">
          Page {currentVariantsPage} of {totalVariantPages}
        </p>
      </div>

      <div className="w-full overflow-hidden">
        <table className="w-full table-fixed border-collapse text-xs xl:text-sm">
          <colgroup>
            <col className="w-[18%]" />
            <col className="w-[13%]" />
            <col className="w-[11%]" />
            <col className="w-[16%]" />
            <col className="w-[9%]" />
            <col className="w-[10%]" />
            <col className="w-[10%]" />
            <col className="w-[12%]" />
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              <th className="break-words border border-gray-300 px-2 py-4 text-left font-semibold text-gray-900">
                Name
              </th>
              <th className="break-words border border-gray-300 px-2 py-4 text-left font-semibold text-gray-900">
                Format & Size
              </th>
              <th className="whitespace-nowrap border border-gray-300 px-2 py-4 text-center font-semibold text-gray-900">
                Components
              </th>
              <th className="break-words border border-gray-300 px-2 py-4 text-left font-semibold text-gray-900">
                Dimensions
              </th>
              <th className="whitespace-nowrap border border-gray-300 px-2 py-4 text-center font-semibold text-gray-900">
                TI X HI
              </th>
              <th className="break-words border border-gray-300 px-2 py-4 text-center font-semibold text-gray-900">
                Created
              </th>
              <th className="break-words border border-gray-300 px-2 py-4 text-center font-semibold text-gray-900">
                Updated
              </th>
              <th className="break-words border border-gray-300 px-2 py-4 text-center font-semibold text-gray-900">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {sortedVariants.slice(visibleVariantStart,visibleVariantStart+variantsPerPage).map((variant, index) => (
              <tr
                key={variant._id}
                className={`${
                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                } hover:bg-gray-100 transition-colors`}
              >
                <td className="border border-gray-300 px-3 py-5 align-middle">
                  <p className="break-words font-semibold text-gray-900">
                    {variant.name || "—"}
                  </p>
                </td>

                <td className="border border-gray-300 px-3 py-5 align-middle">
                  <span className="inline-block max-w-full break-words bg-black text-white text-[11px] px-2 py-1 rounded-full font-medium">
                    {variant.format || "—"}
                    {variant.size
                      ? ` • ${
                          variant.size === "25kg"
                            ? "Master case"
                            : variant.size
                        }`
                      : ""}
                  </span>

                  {Number(variant.defaultCasePack) > 0 && (
                    <p className="text-[11px] text-gray-600 mt-2">
                      Case Pack: {Number(variant.defaultCasePack)}
                    </p>
                  )}

                  {Number(variant.defaultInnerMaster) > 0 && (
                    <p className="text-[11px] text-gray-600 mt-1">
                      Inner Master: {Number(variant.defaultInnerMaster)}
                    </p>
                  )}
                </td>

                <td className="border border-gray-300 px-3 py-5 text-center align-middle">
                  <span className="inline-flex min-w-[34px] justify-center rounded-md bg-gray-100 px-3 py-1 font-bold text-gray-900">
                    {variant.items?.length || 0}
                  </span>
                </td>

                <td className="border border-gray-300 px-3 py-5 align-middle">
                  {variant.lengthMm || variant.widthMm || variant.heightMm ? (
                    <>
                      <p className="font-semibold text-gray-900">
                        {variant.lengthMm || "—"} × {variant.widthMm || "—"} ×{" "}
                        {variant.heightMm || "—"} mm
                      </p>
                      <p className="text-[11px] text-gray-500 mt-1">
                        (
                        {typeof variant.lengthIn === "number"
                          ? variant.lengthIn.toFixed(3)
                          : "—"}{" "}
                        ×{" "}
                        {typeof variant.widthIn === "number"
                          ? variant.widthIn.toFixed(3)
                          : "—"}{" "}
                        ×{" "}
                        {typeof variant.heightIn === "number"
                          ? variant.heightIn.toFixed(3)
                          : "—"}{" "}
                        in)
                      </p>
                    </>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>

                <td className="whitespace-nowrap border border-gray-300 px-3 py-5 text-center align-middle font-medium text-gray-900">
                  {variant.ti || variant.hi
                    ? `${variant.ti || "—"} x ${variant.hi || "—"}`
                    : "—"}
                </td>

                <td className="border border-gray-300 px-3 py-5 text-center align-middle text-gray-800">
                  {variant.createdAt
                    ? new Date(variant.createdAt).toLocaleDateString()
                    : "—"}
                </td>

                <td className="border border-gray-300 px-3 py-5 text-center align-middle text-gray-800">
                  {variant.updatedAt
                    ? new Date(variant.updatedAt).toLocaleDateString()
                    : "—"}
                </td>

                <td className="border border-gray-300 px-2 py-5 align-middle">
                  <div className="grid grid-cols-3 items-center justify-items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleViewVariant(variant)}
                      className="inline-flex h-7 w-7 items-center justify-center text-gray-900 hover:text-black transition"
                      title="View"
                    >
                      <FaEye className="w-4 h-4" />
                    </button>

                    <button
                      type="button"
                      onClick={() => startEditVariant(variant)}
                      disabled={!canManageCostingSettings}
                      className={`inline-flex h-7 w-7 items-center justify-center transition ${
                        canManageCostingSettings
                          ? "text-blue-600 hover:text-blue-800"
                          : "text-gray-300 cursor-not-allowed"
                      }`}
                      title="Edit"
                    >
                      <FaEdit className="w-4 h-4" />
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDeleteVariant(variant)}
                      disabled={!canManageCostingSettings}
                      className={`inline-flex h-7 w-7 items-center justify-center transition ${
                        canManageCostingSettings
                          ? "text-gray-900 hover:text-red-600"
                          : "text-gray-300 cursor-not-allowed"
                      }`}
                      title="Delete"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    <div className="flex flex-col sm:flex-row justify-end items-center gap-4 mt-6">
      <button
        type="button"
        onClick={() =>
          setCurrentVariantsPage((prev) => Math.max(prev - 1, 1))
        }
        disabled={currentVariantsPage === 1}
        className={`px-6 py-3 rounded-lg text-sm font-semibold border transition ${
          currentVariantsPage === 1
            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
            : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
        }`}
      >
        Previous
      </button>

      <div className="flex items-center gap-3">
        {Array.from({ length: totalVariantPages }, (_, index) => index + 1)
          .filter(
            (page) =>
              page === 1 ||
              page === totalVariantPages ||
              Math.abs(page - currentVariantsPage) <= 1
          )
          .map((page, index, array) => (
            <React.Fragment key={page}>
              {index > 0 && page - array[index - 1] > 1 && (
                <span className="px-2 text-gray-400">...</span>
              )}

              <button
                type="button"
                onClick={() => setCurrentVariantsPage(page)}
                className={`min-w-[42px] px-4 py-3 rounded-lg text-sm font-semibold border transition ${
                  currentVariantsPage === page
                    ? "bg-black text-white border-black"
                    : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {page}
              </button>
            </React.Fragment>
          ))}
      </div>

      <button
        type="button"
        onClick={() =>
          setCurrentVariantsPage((prev) =>
            Math.min(prev + 1, totalVariantPages)
          )
        }
        disabled={currentVariantsPage === totalVariantPages}
        className={`px-6 py-3 rounded-lg text-sm font-semibold border transition ${
          currentVariantsPage === totalVariantPages
            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
            : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
        }`}
      >
        Next
      </button>
    </div>
  </>
)}
              </div>
            </div>
          )}
        </div>

      {/* View Variant Items Modal */}
      {viewModalOpen && viewVariantData && (() => {
        const items = Array.isArray(viewVariantData.items) ? viewVariantData.items : [];
        const grandTotal = items.reduce((sum, item) => {
          const qty = Number(item.qty) || 0;
          const cost = Number(item.unitCost) || 0;
          return sum + qty * cost;
        }, 0);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {viewVariantData.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {viewVariantData.format && (
                      <span className="inline-block bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full mr-2">
                        {viewVariantData.format}
                      </span>
                    )}
                    {items.length} item{items.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <button
                  onClick={() => { setViewModalOpen(false); setViewVariantData(null); }}
                  className="text-gray-400 hover:text-gray-700 transition text-2xl leading-none"
                >
                  &times;
                </button>
              </div>

              {/* Items Table */}
              <div className="px-8 py-6 max-h-[60vh] overflow-y-auto">
                {items.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No items in this variant.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 pr-4 font-semibold text-gray-600 w-1/2">Item Name</th>
                        <th className="text-left py-2 pr-4 font-semibold text-gray-600 w-1/4">SKU</th>
                        <th className="text-right py-2 font-semibold text-gray-600 w-1/4">Total Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => {
                        const qty = Number(item.qty) || 0;
                        const unitCost = Number(item.unitCost) || 0;
                        const totalCost = qty * unitCost;
                        return (
                          <tr key={item.id || idx} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 pr-4 text-gray-900 font-medium">
                              {item.name || <span className="text-gray-400 italic">—</span>}
                            </td>
                            <td className="py-3 pr-4 text-gray-500 font-mono text-xs">
                              {item.sku || <span className="text-gray-400 italic">—</span>}
                            </td>
                            <td className="py-3 text-right text-gray-900 font-medium">
                              ${totalCost.toFixed(3)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Footer with Grand Total */}
              <div className="flex items-center justify-between px-8 py-5 bg-gray-50 border-t border-gray-200">
                <span className="text-sm font-semibold text-gray-700">Grand Total Cost</span>
                <span className="text-xl font-bold text-gray-900">${grandTotal.toFixed(3)}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                Delete Variant?
              </h3>
            </div>

            <p className="text-gray-600 mb-8 leading-relaxed">
              Are you sure you want to delete the packaging variant{" "}
              <span className="font-semibold text-gray-900">
                "{variantToDelete?.name}"
              </span>
              ?<br />
              <span className="text-red-600 font-medium">
                This action cannot be undone.
              </span>
            </p>

            <div className="flex justify-end gap-4">
              <button
                onClick={cancelDelete}
                className="px-6 py-3 bg-gray-200 text-gray-800 font-semibold rounded-xl hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={!canManageCostingSettings}
                className="px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition shadow-lg active:scale-98"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* View Variant Items Modal */}
      {viewVariantModalOpen && viewingVariant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full mx-4 animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {viewingVariant.name}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {viewingVariant.format} &mdash; {viewingVariant.items?.length || 0} item{viewingVariant.items?.length !== 1 ? "s" : ""}
                </p>
              </div>
              <button
                onClick={() => {
                  setViewVariantModalOpen(false);
                  setViewingVariant(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition rounded-full p-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Items Table */}
            {viewingVariant.items?.length > 0 ? (
              <div className="overflow-hidden rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 w-8">#</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">SKU</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Item Name</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {viewingVariant.items.map((item, idx) => (
                      <tr key={item.id || idx} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-gray-400 text-xs">{idx + 1}</td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-700">
                          {item.sku || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-800">
                          {item.name || <span className="text-gray-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-10 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 mx-auto mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="text-sm">No items in this variant</p>
              </div>
            )}


            

            {/* Footer */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setViewVariantModalOpen(false);
                  setViewingVariant(null);
                }}
                className="px-6 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

